import React, { Component } from 'react';

import { Grid, Paper, TextField } from '@material-ui/core';
import AddCircle from '@material-ui/icons/AddCircle';
import Select from 'react-select';

import { ICreateManagement } from '../../../../../../store/admin/clubs/actions';
import {
  IClubOfficer,
  IClubOfficersStore,
  INewClubOfficerForm
} from '../../../../../../store/admin/clubs/store';
import {
  IAdminMembership,
  IAdminMembershipsStore
} from '../../../../../../store/admin/memberships/store';
import { IMessageNotification } from '../../../../../../store/notification/store';
import { TPushNotificationAction } from '../../../../../../store/notification/Actions/push';

import Button from '../../../../../../components/Button';
import YesNoSwitch from '../../../../../../components/YesNoSwitch';
import { selectorTheme } from '../../../../../../contrib/utils';
import { getOfficerPermissionsValue } from '../Utils/getOfficerPermissionsValue';

export interface INewOfficerProps {
  clubId: number;
  clubManagement: IClubOfficersStore;
  createManagement: ICreateManagement;
  memberships: IAdminMembershipsStore;
  pushNotification: TPushNotificationAction;
}

export interface INewOfficerState {
  displayForm: boolean;
  officer: INewClubOfficerForm;
}

interface ISelectOption {
  label: string;
  value: number;
}

export default class NewOfficer extends Component<INewOfficerProps, INewOfficerState> {
  constructor(props: INewOfficerProps) {
    super(props);

    this.state = {
      displayForm: false,
      officer: this.getDefaultFormOfficer()
    };
  }

  public getDefaultFormOfficer = (): INewClubOfficerForm => ({
    club: this.props.clubId,
    isAdmin: false,
    isStaff: false,
    position: '',
    status: 'inactive',
    user: -1
  });

  public setAdminPermission = (value: any) => {
    const { officer } = this.state;
    const { label } = value;

    const isStaff = label === 'STAFF' ? true : false;
    const isAdmin = label === 'ADMIN' ? true : false;

    this.setState({ officer: { ...officer, isStaff, isAdmin } });
  };

  public selectOfficerName = (value: any) => {
    const { officer } = this.state;
    const { value: user } = value;

    this.setState({ officer: { ...officer, user } });
  };

  public setPosition = (event: any): void => {
    const { officer } = this.state;
    const {
      target: { value: position }
    } = event;

    this.setState({ officer: { ...officer, position } });
  };

  public toggleActiveOfficer = (_: any, value: boolean) => {
    const { officer } = this.state;
    const status = value ? 'active' : 'inactive';

    this.setState({ officer: { ...officer, status } });
  };

  public toggleForm = () => this.setState({ displayForm: !this.state.displayForm });

  public saveAction = async () => {
    const { createManagement, pushNotification } = this.props;
    const { officer } = this.state;

    const response = await createManagement(officer);

    if (response.errors) {
      await pushNotification(({
        status: 'unread',
        text: response.errors.adminClubs.msg,
        type: 'error'
      } as unknown) as IMessageNotification);
    } else {
      await pushNotification(({
        status: 'unread',
        text: 'Membership created!',
        type: 'success'
      } as unknown) as IMessageNotification);

      this.toggleForm();
      this.resetForm();
    }
  };

  public resetForm = () => {
    this.setState({ officer: this.getDefaultFormOfficer() });
  };

  public cancelAction = () => {
    const { displayForm } = this.state;

    if (displayForm) {
      this.toggleForm();
    }
    this.resetForm();
  };

  public getNameOptions = (): any => {
    const {
      clubManagement,
      memberships: { memberships }
    } = this.props;

    const clubManagementUserIds = Object.values(clubManagement).map(
      (clubOfficer: IClubOfficer) => clubOfficer.user.id
    );

    return memberships
      .map(
        (membership: IAdminMembership): ISelectOption => ({
          label: `${membership.user.firstName} ${membership.user.lastName}`,
          value: membership.user.id
        })
      )
      .filter((option: ISelectOption) => clubManagementUserIds.indexOf(option.value) < 0);
  };

  public getNameValue = () => {
    const {
      officer: { user }
    } = this.state;

    const {
      memberships: { memberships }
    } = this.props;

    const officerSelectedMembership = memberships.filter(
      (membership: IAdminMembership): any => membership.user.id === user
    );

    return officerSelectedMembership.length > 0
      ? {
          label: `${officerSelectedMembership[0].user.firstName} ${
            officerSelectedMembership[0].user.lastName
          }`,
          value: officerSelectedMembership[0].user.id
        }
      : { label: 'Select...', value: '-1' };
  };

  public render(): JSX.Element {
    const {
      displayForm,
      officer: { position, status, user },
      officer
    } = this.state;

    const {
      memberships: { memberships }
    } = this.props;

    return displayForm ? (
      <Paper elevation={2} className="paper row officer-row new-officer">
        <Grid className="grid" container={true} spacing={8}>
          <Grid className="grid-item select" item={true} xs={12} md={3}>
            <div className="item-container">
              <Select
                className="autocomplete"
                classNamePrefix="autocomplete"
                value={this.getNameValue()}
                onChange={this.selectOfficerName}
                options={this.getNameOptions()}
                theme={selectorTheme}
              />
            </div>
          </Grid>
          <Grid className="grid-item" item={true} xs={12} md={2}>
            <TextField
              id="new-officer-position"
              className="position-field"
              placeholder="Position"
              defaultValue={position}
              onChange={this.setPosition}
              margin="normal"
              inputProps={{ 'aria-label': 'bare' }}
            />
          </Grid>
          <Grid className="grid-item" item={true} xs={12} md={2}>
            <YesNoSwitch checked={status === 'active'} onChange={this.toggleActiveOfficer} />
          </Grid>
          <Grid className="grid-item" item={true} xs={12} md={2}>
            <div className="item-container">
              <Select
                className="autocomplete"
                classNamePrefix="autocomplete"
                value={getOfficerPermissionsValue(officer)}
                onChange={this.setAdminPermission}
                options={[
                  { label: 'ADMIN', value: 'admin' },
                  { label: 'STAFF', value: 'staff' },
                  { label: 'NONE', value: 'none' }
                ]}
                theme={selectorTheme}
              />
            </div>
          </Grid>
          <Grid className="grid-item buttons" item={true} xs={12} md={3}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={this.saveAction}
              disabled={user === -1}
            >
              Save
            </Button>
            <Button variant={'outlined'} color="primary" type="submit" onClick={this.cancelAction}>
              Cancel
            </Button>
          </Grid>
        </Grid>
      </Paper>
    ) : memberships.length > 0 ? (
      <Button className="add-another" color="primary" onClick={this.toggleForm}>
        <AddCircle style={{ fontSize: 30 }} />
        ADD ANOTHER ADMIN/OFFICER
      </Button>
    ) : (
      <div />
    );
  }
}
